// ============================================================================
// Correcting the two dates on a separation.
//
// ⛔ WHY THIS EXISTS AT ALL. Three people left and no date was ever written
// down — they were offboarded by flipping a status, so there was nothing to
// write it on. Now there is a record, and the person who knows the answer
// (Antonela) needs to be able to put it there. Ben, 2026-08-26: "This should be
// editable on their record."
//
// ⛔ THE PAYROLL REPORTS GIVE A PERIOD, NOT A DAY. They are bi-weekly. What the
// last period each person worked hours in is a hard fact and it is on the record
// as a note, shown here beside the field. The exact last day inside that period
// is not in them and is deliberately not pre-filled — a date that looks recorded
// but was inferred is worse than an empty one.
// ============================================================================

import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Button, Card, useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { updateSeparationDates } from './hooks/use-separation-data';
import type { SeparationRecord } from './types';

export interface EditDatesProps {
  record: SeparationRecord;
  canEdit: boolean;
  onSaved: () => void;
}

export function MissingDateBanner({ record, canEdit, onSaved }: EditDatesProps) {
  const [open, setOpen] = useState(false);
  if (record.last_working_day) return null;
  return (
    <>
      <Card className="border-sentiment-warning-text/40 bg-sentiment-warning-fill p-4">
        <div className="flex items-start gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-sentiment-warning-text" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-sentiment-warning-text">
              No last working day recorded
            </div>
            <p className="mt-1 text-sm text-tcs-text-secondary">
              This departure predates the separation module, so the date was never written
              down anywhere. It has not been guessed. The payroll evidence below narrows it
              to a two-week period — set the actual day.
            </p>
            {canEdit && (
              <Button size="sm" className="mt-2" onClick={() => setOpen(true)}>
                Set the dates
              </Button>
            )}
          </div>
        </div>
      </Card>
      {open && (
        <EditDatesModal
          record={record}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      )}
    </>
  );
}

export function EditDatesButton({ record, canEdit, onSaved }: EditDatesProps) {
  const [open, setOpen] = useState(false);
  if (!canEdit) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon={<CalendarClock className="h-4 w-4" />}
        onClick={() => setOpen(true)}
      >
        Edit dates
      </Button>
      {open && (
        <EditDatesModal
          record={record}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      )}
    </>
  );
}

function EditDatesModal({
  record,
  onClose,
  onSaved,
}: {
  record: SeparationRecord;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [lastDay, setLastDay] = useState(record.last_working_day ?? '');
  const [notice, setNotice] = useState(record.notice_date ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // The payroll evidence lives in the record's notes, put there when the
  // separation was backfilled. Shown verbatim rather than summarised: the
  // person setting the date should read what it actually says, including the
  // warning about a row that looks copied forward.
  const evidence = (record.notes ?? '')
    .split('\n')
    .filter((l) => l.startsWith('Payroll evidence'));

  async function save() {
    setBusy(true);
    try {
      await updateSeparationDates(
        record.separation_id,
        lastDay || null,
        notice || null,
        note.trim() || null,
      );
      toast.success('Dates recorded');
      onSaved();
    } catch (err) {
      toast.error("Couldn't save the dates", {
        description: errorMessage(err, 'Try again.'),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit separation dates"
        className="w-full max-w-lg rounded-lg bg-tcs-surface p-5 shadow-lg"
      >
        <h2 className="text-base font-semibold text-tcs-text">
          Dates — {record.separation_id}
        </h2>
        <p className="mt-1 text-xs text-tcs-text-secondary">
          Correcting a date does not reopen the separation or re-run anything. It records
          what happened and keeps the roster in step.
        </p>

        {evidence.length > 0 && (
          <div className="mt-3 rounded border border-tcs-border bg-tcs-bg-muted p-3">
            <div className="text-xs font-medium text-tcs-text">From the payroll reports</div>
            {evidence.map((line) => (
              <p key={line} className="mt-1 text-xs text-tcs-text-secondary">
                {line.replace(/^Payroll evidence[^:]*:\s*/, '')}
              </p>
            ))}
          </div>
        )}

        <label className="mt-4 block text-sm text-tcs-text-secondary">
          Last working day
          <input
            type="date"
            value={lastDay}
            onChange={(e) => setLastDay(e.target.value)}
            className="mt-1 w-full rounded border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text"
          />
        </label>
        <label className="mt-3 block text-sm text-tcs-text-secondary">
          Notice given
          <input
            type="date"
            value={notice}
            onChange={(e) => setNotice(e.target.value)}
            className="mt-1 w-full rounded border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text"
          />
        </label>
        <label className="mt-3 block text-sm text-tcs-text-secondary">
          Where the date came from — optional
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Confirmed against the July hour report"
            className="mt-1 w-full rounded border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
